// ─────────────────────────────────────────────────────────────────────────────
// Heures spéciales & avantages employeur — fonctions pures, sans Firestore
// (testables, et importables côté 'use client' : ne dépend que de projection.ts
// et d'un type — aucune dépendance firebase-admin).
//
// Une période d'heures spéciales est bornée sur des semaines de paie :
// start = un DIMANCHE, end = un SAMEDI. Le coût applique le temps et demi (1,5×)
// au-delà de 40 h/sem, au taux horaire courant de l'employé.
// ─────────────────────────────────────────────────────────────────────────────

import type { HoursOverride, RatePremium } from '../types/firestore'

import { round2 } from './projection'

const MS_PER_DAY = 24 * 60 * 60 * 1000
const BIWEEKLY_PERIODS_PER_YEAR = 26 // 52 semaines / 2
const OVERTIME_THRESHOLD = 40 // heures/sem au-delà desquelles le temps et demi s'applique
const OVERTIME_MULTIPLIER = 1.5

function toUtc(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number)
  return Date.UTC(y, m - 1, d)
}

function toIso(utcMs: number): string {
  return new Date(utcMs).toISOString().slice(0, 10)
}

// Nombre de jours entre deux dates ISO, bornes incluses.
function inclusiveDays(startIso: string, endIso: string): number {
  return Math.round((toUtc(endIso) - toUtc(startIso)) / MS_PER_DAY) + 1
}

// Jour de la semaine UTC : 0 = dimanche … 6 = samedi.
function utcDay(iso: string): number {
  return new Date(toUtc(iso)).getUTCDay()
}

export function isSunday(iso: string): boolean {
  return utcDay(iso) === 0
}

export function isSaturday(iso: string): boolean {
  return utcDay(iso) === 6
}

// Dimanche de la semaine contenant `iso` (≤ iso).
export function weekSunday(iso: string): string {
  return toIso(toUtc(iso) - utcDay(iso) * MS_PER_DAY)
}

// Samedi de la semaine contenant `iso` (≥ iso).
export function weekSaturday(iso: string): string {
  return toIso(toUtc(iso) + (6 - utcDay(iso)) * MS_PER_DAY)
}

// Cale une date sur sa semaine de paie : { sunday, saturday }.
export function snapToWeek(iso: string): { sunday: string; saturday: string } {
  return { sunday: weekSunday(iso), saturday: weekSaturday(iso) }
}

// Nombre de semaines d'une période dimanche→samedi (multiple de 7 garanti).
export function weeksInPeriod(startIso: string, endIso: string): number {
  return inclusiveDays(startIso, endIso) / 7
}

// Coût hebdomadaire avec temps et demi au-delà de 40 h.
export function weeklyCostWithOvertime(weeklyHours: number, hourlyRate: number): number {
  const regular = Math.min(weeklyHours, OVERTIME_THRESHOLD) * hourlyRate
  const overtime =
    Math.max(weeklyHours - OVERTIME_THRESHOLD, 0) * hourlyRate * OVERTIME_MULTIPLIER
  return round2(regular + overtime)
}

// Coût total d'une période = coût hebdomadaire × nombre de semaines.
export function periodCost(override: HoursOverride, hourlyRate: number): number {
  const weeks = weeksInPeriod(override.start_date, override.end_date)
  return round2(weeklyCostWithOvertime(override.weekly_hours, hourlyRate) * weeks)
}

// Supplément annuel des périodes spéciales vs les heures de base, borné à
// l'année civile (les jours hors année sont exclus). C'est l'addend à ajouter à
// la masse salariale : le salaire de base couvre déjà `baseWeeklyHours` toute
// l'année — on ne compte ici que le SURCOÛT des semaines spéciales.
export function overridesSupplement(
  overrides: HoursOverride[],
  baseWeeklyHours: number,
  hourlyRate: number,
  year: number,
  periodStart?: string,
  periodEnd?: string,
): number {
  const yearStart = `${year}-01-01`
  const yearEnd = `${year}-12-31`
  // Bornes effectives = sous-période demandée clippée à l'année civile
  // (défaut = année pleine ⇒ comportement d'origine).
  const boundStart = periodStart && periodStart > yearStart ? periodStart : yearStart
  const boundEnd = periodEnd && periodEnd < yearEnd ? periodEnd : yearEnd
  const baselineWeekly = weeklyCostWithOvertime(baseWeeklyHours, hourlyRate)
  let total = 0
  for (const o of overrides) {
    const start = o.start_date > boundStart ? o.start_date : boundStart
    const end = o.end_date < boundEnd ? o.end_date : boundEnd
    if (start > end) continue // période entièrement hors de la fenêtre
    const days = inclusiveDays(start, end)
    const overrideWeekly = weeklyCostWithOvertime(o.weekly_hours, hourlyRate)
    const dailyDelta = (overrideWeekly - baselineWeekly) / 7
    total += dailyDelta * days
  }
  return round2(total)
}

// Supplément annuel des primes ponctuelles, borné à l'année civile. La prime
// ($/h) s'applique À PLAT sur les heures RÉELLEMENT planifiées : heures de base,
// remplacées par les heures spéciales le cas échéant (les périodes prime↔heures
// spéciales se chevauchent volontairement ici). Aucune majoration 1,5× sur la
// prime. C'est un addend à ajouter à la masse salariale — il ne dépend pas du
// taux horaire (la prime est un montant absolu).
export function premiumsSupplement(
  premiums: RatePremium[],
  baseWeeklyHours: number,
  overrides: HoursOverride[],
  year: number,
  periodStart?: string,
  periodEnd?: string,
): number {
  const yearStart = `${year}-01-01`
  const yearEnd = `${year}-12-31`
  // Bornes effectives = sous-période demandée clippée à l'année civile
  // (défaut = année pleine ⇒ comportement d'origine).
  const boundStart = periodStart && periodStart > yearStart ? periodStart : yearStart
  const boundEnd = periodEnd && periodEnd < yearEnd ? periodEnd : yearEnd
  let total = 0
  for (const p of premiums) {
    const start = p.start_date > boundStart ? p.start_date : boundStart
    const end = p.end_date < boundEnd ? p.end_date : boundEnd
    if (start > end) continue // période entièrement hors de la fenêtre
    // Heures travaillées sur la période = heures de base, ajustées des heures
    // spéciales sur les jours chevauchés (les périodes spéciales sont disjointes).
    let hours = (baseWeeklyHours / 7) * inclusiveDays(start, end)
    for (const o of overrides) {
      const os = o.start_date > start ? o.start_date : start
      const oe = o.end_date < end ? o.end_date : end
      if (os > oe) continue
      hours += ((o.weekly_hours - baseWeeklyHours) / 7) * inclusiveDays(os, oe)
    }
    total += p.premium_per_hour * hours
  }
  return round2(total)
}

// Coût annuel des avantages employeur. Tous les montants sont PAR 2 SEMAINES
// (assurance, cellulaire, allocation, véhicule) → annualisés × 26.
export function employerBenefitsAnnual(input: {
  insurance_biweekly: number | null
  cell_allowance_biweekly: number | null
  other_allowance_biweekly: number | null
  has_company_vehicle: boolean
  vehicle_cost_biweekly: number | null
}): number {
  const allowancesBiweekly =
    (input.insurance_biweekly ?? 0) +
    (input.cell_allowance_biweekly ?? 0) +
    (input.other_allowance_biweekly ?? 0)
  const vehicleBiweekly = input.has_company_vehicle
    ? (input.vehicle_cost_biweekly ?? 0)
    : 0
  return round2((allowancesBiweekly + vehicleBiweekly) * BIWEEKLY_PERIODS_PER_YEAR)
}

// La période est-elle active à la date `todayIso` (bornes incluses) ?
export function isPeriodActive(override: HoursOverride, todayIso: string): boolean {
  return override.start_date <= todayIso && todayIso <= override.end_date
}

// Deux périodes [aStart,aEnd] et [bStart,bEnd] (bornes incluses, dates ISO
// 'YYYY-MM-DD' comparées lexicographiquement) se chevauchent-elles ? Le supplément
// d'heures spéciales (overridesSupplement) suppose des périodes disjointes : un
// chevauchement double-compterait le surcoût, d'où cette garde à la saisie.
export function periodsOverlap(
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string,
): boolean {
  return aStart <= bEnd && bStart <= aEnd
}

// Une période candidate chevauche-t-elle une période existante ?
export function overlapsAny(
  candidate: { start_date: string; end_date: string },
  existing: { start_date: string; end_date: string }[],
): boolean {
  return existing.some((o) =>
    periodsOverlap(candidate.start_date, candidate.end_date, o.start_date, o.end_date),
  )
}
